/**
 * Readiness signal for Tealium's utag.js.
 *
 * utag.js is injected with a deferred strategy, so anything wanting to emit an event
 * on page load races the script. Instead of polling for `window.utag`, the loader
 * resolves a single deferred promise once the script has executed (or failed), and
 * callers just await it.
 *
 * The promise is parked on `window` so it stays a true singleton no matter how the
 * loader and its consumers end up chunked.
 */

type UtagApi = {
  view: (data: Record<string, unknown>) => void;
  link: (data: Record<string, unknown>) => void;
};

declare global {
  interface Window {
    __utagReady?: Promise<boolean>;
    __utagReadyResolve?: (loaded: boolean) => void;
  }
}

/** How long to wait for `onLoad`/`onError` before giving up on the script. */
const UTAG_READY_TIMEOUT_MS = 15000;

/**
 * After `onLoad` fires, how long to keep polling for `window.utag.view` to become
 * callable. Tealium's utag.js may defer exposing its API via `requestIdleCallback`,
 * so `onLoad` can fire before `utag.view` is set. Calling `tealiumView()` in that
 * window silently no-ops (the `!window.utag?.view` guard), dropping the
 * hard-navigation page-view event.
 */
const UTAG_VIEW_POLL_MS = 5000;

function getUtag(): UtagApi | undefined {
  return (window as Window & { utag?: UtagApi }).utag;
}

/**
 * Resolves `true` once utag.js has executed AND `window.utag.view` is callable,
 * `false` if it failed to load or never arrived. Never rejects — a blocked script
 * must degrade to a no-op rather than an unhandled rejection in every awaiting effect.
 */
export function utagReady(timeoutMs: number = UTAG_READY_TIMEOUT_MS): Promise<boolean> {
  if (typeof window === "undefined") return Promise.resolve(false);

  // An in-flight promise always wins, so a late caller can never replace the one the
  // loader is holding a resolver for.
  if (window.__utagReady) return window.__utagReady;

  // Already executed — a cached script, or a consumer mounting after a client-side
  // navigation. Nothing to wait for.
  if (getUtag()?.view) {
    window.__utagReady = Promise.resolve(true);
    return window.__utagReady;
  }

  window.__utagReady = new Promise<boolean>((resolve) => {
    window.__utagReadyResolve = resolve;
    // Backstop: if neither onLoad nor onError ever fires, unblock the awaiters
    // instead of leaving them pending for the life of the page.
    window.setTimeout(() => resolve(false), timeoutMs);
  });

  return window.__utagReady;
}

/**
 * Called by the Tealium loader from `onLoad` / `onError`. Safe to call before any
 * consumer has awaited `utagReady()` — the promise is created on demand.
 *
 * When `loaded` is true and `utag.view` is not immediately callable, this polls via
 * `requestAnimationFrame` rather than resolving straight away. Tealium's utag.js can
 * defer its API setup to a `requestIdleCallback`, meaning `onLoad` fires before
 * `window.utag.view` is set. Without the poll, `tealiumView()`'s
 * `!window.utag?.view` guard would silently drop every hard-navigation page-view.
 */
export function markUtagLoaded(loaded: boolean): void {
  if (typeof window === "undefined") return;

  void utagReady();

  if (!loaded) {
    window.__utagReadyResolve?.(false);
    window.__utagReadyResolve = undefined;
    return;
  }

  // Fast path: utag.view is already callable (cached script or synchronous init).
  if (getUtag()?.view) {
    window.__utagReadyResolve?.(true);
    window.__utagReadyResolve = undefined;
    window.__utagReady = Promise.resolve(true);
    return;
  }

  // Slow path: onLoad fired but utag.view is not yet set. Poll with rAF until the
  // API is callable or the deadline passes. The 15 s backstop timeout inside
  // utagReady() still runs concurrently; whichever settles the promise first wins —
  // calling resolve() on an already-settled Promise is a safe no-op.
  const resolve = window.__utagReadyResolve;
  window.__utagReadyResolve = undefined;

  const deadline = Date.now() + UTAG_VIEW_POLL_MS;

  const poll = () => {
    if (getUtag()?.view) {
      resolve?.(true);
      // Recover the happy path: future utagReady() callers (e.g. after a soft nav)
      // get an immediately-resolving promise rather than the now-stale deferred one.
      window.__utagReady = Promise.resolve(true);
    } else if (Date.now() < deadline) {
      requestAnimationFrame(poll);
    } else {
      resolve?.(false);
    }
  };

  requestAnimationFrame(poll);
}
